import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axios from "axios";
import { publicRequest } from "../api/requestMethods";

// Create Slice for Listing

const initialState = {
  listings: [],
  promoListings: [],
  currentPromoProduct: null,
  numberOfPages: 0,
  currentPage: 1,
  total: 0,
  // Facet counts for the unified storefront rail (vendorType, category, price, rating)
  facets: null,
  isFetching: false,
  fetched: false,
  error: null,
  // Cache for fetched categories to avoid unnecessary refetching
  categoryCache: {},
  // Cache duration in milliseconds (5 minutes)
  cacheDuration: 5 * 60 * 1000,
};

// Create cached version of fetchListings
export const fetchListingsWithCache = (page, selectedCategory) => {
  return (dispatch, getState) => {
    const state = getState();
    const cacheKey = `${selectedCategory}-${page}`;
    const cachedData = state.products?.categoryCache?.[cacheKey];
    const now = Date.now();
    
    // Check if we have valid cached data
    if (cachedData && (now - cachedData.timestamp) < state.products?.cacheDuration) {
      // Use cached data by updating the state with cached values
      dispatch(listingsFetchedFromCache({
        data: cachedData.data,
        currentPage: cachedData.currentPage,
        numberOfPages: cachedData.numberOfPages,
        cacheKey
      }));
      return Promise.resolve(cachedData);
    }
    
    // No valid cache, fetch fresh data
    return dispatch(fetchListings({ page, selectedCategory }));
  };
};

export const fetchListings = createAsyncThunk(
  "listing/fetchListings",
  async (passedData) => {
    // console.log(page, "Pagerr....");
    const { page, selectedCategory } = passedData;
    const {
      data: { data, currentPage, numberOfPages },
    } = await publicRequest(
      // Encoded: a category name may carry "&" ("Tea & Coffee").
      `/posts/web?page=${page}&category=${encodeURIComponent(selectedCategory ?? "")}`
    );
    return { data, currentPage, numberOfPages };
  }
);

// Get Promo Listings and Deals
export const fetchPromoListings = createAsyncThunk(
  "listing/fetchPromoListings",
  async () => {
    const {
      data: { data  },
    } = await publicRequest("/posts/web/promo");
    return { data };
  }
);

// An asyncThunk for fetching a single promo product's details
export const fetchPromoProductDetails = createAsyncThunk(
  "listing/fetchPromoProductDetails",
  async (promoProductId, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get(`/posts/promo-detail/${promoProductId}`);
      return response.data;
    } catch (error) {
      // console.log(error.response.data.message, "errrrr>")
        return rejectWithValue(error.response.data.message);
    }
  }
);


// EndPoint for fetching all listings for All Products without categorization
export const fetchAllListingsNoCategories = createAsyncThunk(
  "listing/fetchAllListingsNoCategories",
  async (passedData) => {
    const { page } = passedData;
    const {
      data: { data, currentPage, numberOfPages },
    } = await publicRequest(`/posts/web?page=${page}`);
    return { data, currentPage, numberOfPages };
  }
);

export const fetchListingsBySearch = createAsyncThunk(
  "listing/fetchListingsBySearch",
  async (searchQuery) => {
    const {
      data: { data },
    } = await publicRequest(
      `/posts/webproduct_search?searchQuery=${searchQuery.search || "none"}`
    );

    return { data };
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Unified multi-vendor storefront feed (in-house + retailer + wholesaler +
// brand) with server-side faceted filtering, sorting and ratings.
// Backed by GET /posts/catalog. `params` is a flat object of query params:
//   page, limit, category, search, priceMin, priceMax, vendorType (csv),
//   vendorId, inStock, ratingMin, sort, facets
// ─────────────────────────────────────────────────────────────────────────────
const buildCatalogQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" ) return;
    if (Array.isArray(v)) {
      if (v.length) qs.set(k, v.join(","));
    } else {
      qs.set(k, v);
    }
  });
  return qs.toString();
};

export const fetchCatalog = createAsyncThunk(
  "listing/fetchCatalog",
  async (params = {}) => {
    const qs = buildCatalogQuery({ facets: 1, ...params });
    const { data } = await publicRequest(`/posts/catalog?${qs}`);
    return data; // { data, currentPage, numberOfPages, total, facets }
  }
);

const listingSlice = createSlice({
  name: "listing",
  initialState,
  reducers: {
    listingsFetched: (state, action) => {
      state.listings = action.payload;
    },
    listingsFetchedFromCache: (state, action) => {
      const { data, currentPage, numberOfPages, cacheKey } = action.payload;
      state.listings = data;
      state.currentPage = currentPage;
      state.numberOfPages = numberOfPages;
      state.isFetching = false;
      state.fetched = true;
      // Update cache access time for LRU-style management
      if (state.categoryCache[cacheKey]) {
        state.categoryCache[cacheKey].lastAccessed = Date.now();
      }
    },
    clearCategoryCache: (state, action) => {
      if (action.payload) {
        // Clear specific category cache
        const { category, page } = action.payload;
        const cacheKey = `${category}-${page}`;
        delete state.categoryCache[cacheKey];
      } else {
        // Clear all category cache
        state.categoryCache = {};
      }
    },
    clearCurrentListings: (state) => {
      state.listings = [];
      state.fetched = false;
    },
    restoreFromCache: (state, action) => {
      const { cacheKey } = action.payload;
      const cachedData = state.categoryCache[cacheKey];
      if (cachedData) {
        state.listings = cachedData.data;
        state.currentPage = cachedData.currentPage;
        state.numberOfPages = cachedData.numberOfPages;
        state.isFetching = false;
        state.fetched = true;
        // Update access time
        cachedData.lastAccessed = Date.now();
      }
    },
    // Realtime removal for the catalog:public `product:statusChanged` socket
    // event: an item a vendor just DEACTIVATED disappears from the live grid
    // and every cached category page immediately. (Re-activations refetch —
    // the server stays the source of truth for what re-enters the grid.)
    removeCatalogItem: (state, action) => {
      const productId = String(action.payload || "");
      if (!productId) return;
      const before = state.listings.length;
      state.listings = state.listings.filter(
        (item) => String(item._id) !== productId
      );
      if (state.listings.length !== before && state.total > 0) {
        state.total -= 1;
      }
      Object.keys(state.categoryCache || {}).forEach((key) => {
        const entry = state.categoryCache[key];
        if (entry?.data) {
          entry.data = entry.data.filter(
            (item) => String(item._id) !== productId
          );
        }
      });
    },
    // Realtime removal for `brand:statusChanged` (brand delisted): all of the
    // brand's items leave the grid + caches at once.
    removeCatalogVendorItems: (state, action) => {
      const vendorId = String(action.payload || "");
      if (!vendorId) return;
      const drop = (items = []) =>
        items.filter((item) => String(item.vendorId) !== vendorId);
      const before = state.listings.length;
      state.listings = drop(state.listings);
      state.total = Math.max(0, state.total - (before - state.listings.length));
      Object.keys(state.categoryCache || {}).forEach((key) => {
        const entry = state.categoryCache[key];
        if (entry?.data) entry.data = drop(entry.data);
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchListings.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.listings = action.payload.data;
        state.numberOfPages = action.payload.numberOfPages;
        state.currentPage = action.payload.currentPage;
        state.isFetching = false;
        
        // Store in cache for future use
        const cacheKey = `${action.meta.arg.selectedCategory}-${action.meta.arg.page}`;
        state.categoryCache[cacheKey] = {
          data: action.payload.data,
          currentPage: action.payload.currentPage,
          numberOfPages: action.payload.numberOfPages,
          timestamp: Date.now(),
          lastAccessed: Date.now()
        };
        
        // Optional: Clean old cache entries to prevent memory bloat
        const now = Date.now();
        Object.keys(state.categoryCache).forEach(key => {
          if (now - state.categoryCache[key].timestamp > state.cacheDuration * 2) {
            delete state.categoryCache[key];
          }
        });
        
        // state.error = null;
      })
      .addCase(fetchListings.rejected, (state) => {
        state.error = true;
        state.isFetching = false;
      })
      .addCase(fetchPromoListings.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchPromoListings.fulfilled, (state, action) => {
        state.promoListings = action.payload.data;
        state.isFetching = false;
      })
      .addCase(fetchPromoListings.rejected, (state) => {
        state.error = true;
        state.isFetching = false;
      })
      .addCase(fetchPromoProductDetails.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchPromoProductDetails.fulfilled, (state, action) => {
        state.currentPromoProduct = action.payload; 
        state.isFetching = false;
      })
      .addCase(fetchPromoProductDetails.rejected, (state, action) => {
        state.error = action.payload;
        state.isFetching = false;
      })
      .addCase(fetchAllListingsNoCategories.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchAllListingsNoCategories.fulfilled, (state, action) => {
        state.listings = action.payload.data;
        state.numberOfPages = action.payload.numberOfPages;
        state.currentPage = action.payload.currentPage;
        state.isFetching = false;
      })
      .addCase(fetchAllListingsNoCategories.rejected, (state, action) => {
        state.error = true;
        state.isFetching = false;
      })
      .addCase(fetchListingsBySearch.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchListingsBySearch.fulfilled, (state, action) => {
        state.listings = action.payload.data;
        state.numberOfPages = action.payload.numberOfPages;
        state.currentPage = action.payload.currentPage;
        state.isFetching = false;
        state.fetched = true;
        state.error = null;
      })
      .addCase(fetchListingsBySearch.rejected, (state, action) => {
        state.error = action.error;
        state.isFetching = false;
        state.fetched = true;
      })
      .addCase(fetchCatalog.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        state.listings = action.payload.data || [];
        state.numberOfPages = action.payload.numberOfPages || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.total = action.payload.total || 0;
        // Keep last non-null facets so the rail doesn't flicker between fetches.
        if (action.payload.facets) state.facets = action.payload.facets;
        state.isFetching = false;
        state.fetched = true;
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        state.error = action.error?.message || true;
        state.isFetching = false;
        state.fetched = true;
      });
  },
});

export const { listingsFetched, listingsFetchedFromCache, clearCategoryCache, clearCurrentListings, restoreFromCache, removeCatalogItem, removeCatalogVendorItems } = listingSlice.actions;

export default listingSlice.reducer;
